import type { FileAssignmentRecord } from '../../../../domain/src/lib/repository/file-assignment.model';
import {
  filterOaisAssignableAssignments,
  formatRepositoryContextPath,
  isDipAssignablePath,
  isLikelyRepositoryFilePath,
} from '../repository-admin/repository-assignment-path.util';
import {
  resolveAssignmentEntryListPath,
  resolveMyFileAssignmentsFromApi,
} from '../repository-admin/repository-assignment-access.util';

export interface DashboardTaskNavigation {
  route: string[];
  queryParams: Record<string, string>;
  moduleLabel: string;
  linkLabel: string;
}

function readString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

/** Tarea de asignación de archivo/carpeta del repositorio (notificación al usuario consulta). */
export function isFileAssignmentTask(row: Record<string, unknown>): boolean {
  const title = (readString(row, ['title']) ?? '').toLowerCase();
  const type = (
    readString(row, ['type', 'entity_type', 'module', 'category']) ?? ''
  ).toLowerCase();
  const description = (readString(row, ['description']) ?? '').toLowerCase();

  if (title.includes('archivo asignado') || title.includes('nuevo archivo')) {
    return true;
  }
  if (title.includes('bóveda digital') || title.includes('boveda digital')) {
    return true;
  }
  if (type.includes('file_assignment') || type.includes('file-assignment')) {
    return true;
  }
  if (type.includes('repository') || type.includes('repositorio')) {
    return true;
  }
  if (description.includes('ruta:') && (description.includes('/dip/') || description.includes('.zip'))) {
    return true;
  }
  return Boolean(readString(row, ['file_path', 'repository_path', 'target_path']));
}

/** Convierte ruta absoluta del filesystem a path relativo del repositorio OAIS. */
export function normalizeRepositoryTaskPath(raw: string): string {
  let path = raw.trim().replace(/\\/g, '/').replace(/\/+/g, '/');
  const lower = path.toLowerCase();
  const repoMarker = '/repositorio/';
  const repoIdx = lower.indexOf(repoMarker);
  if (repoIdx >= 0) {
    path = path.slice(repoIdx + repoMarker.length);
  } else if (lower.startsWith('repositorio/')) {
    path = path.slice('repositorio/'.length);
  }
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

export function extractRepositoryPathFromTask(row: Record<string, unknown>): string | null {
  const direct = readString(row, [
    'file_path',
    'repository_path',
    'target_path',
    'path',
    'resource_path',
  ]);
  if (direct) {
    return normalizeRepositoryTaskPath(direct);
  }

  const description = readString(row, ['description']) ?? '';
  const routeMatch = description.match(/Ruta:\s*(\S+)/i);
  if (routeMatch?.[1]) {
    return normalizeRepositoryTaskPath(routeMatch[1]);
  }

  const link = readString(row, ['link']);
  if (link) {
    const fromQuery = extractTargetFromUrl(link);
    if (fromQuery) {
      return normalizeRepositoryTaskPath(fromQuery);
    }
  }

  return null;
}

function extractTargetFromUrl(url: string): string | null {
  const queryIdx = url.indexOf('?');
  if (queryIdx < 0) {
    return null;
  }
  const params = new URLSearchParams(url.slice(queryIdx + 1));
  return (
    params.get('target') ||
    params.get('path') ||
    params.get('file_path') ||
    params.get('file') ||
    null
  );
}

function parseVaultRoute(link: string): DashboardTaskNavigation {
  const [pathname, search = ''] = link.split('?');
  const route = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const queryParams: Record<string, string> = {};
  if (search) {
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      queryParams[key] = value;
    });
  }
  return {
    route: [route],
    queryParams,
    moduleLabel: 'Centro de control',
    linkLabel: 'Abrir módulo',
  };
}

function mapLegacyVaultLink(link: string | null): DashboardTaskNavigation {
  if (!link) {
    return {
      route: ['/layout/vault/dashboard'],
      queryParams: {},
      moduleLabel: 'Centro de control',
      linkLabel: 'Abrir módulo',
    };
  }

  const trimmed = link.trim();

  if (trimmed.startsWith('/layout/vault/')) {
    const nav = parseVaultRoute(trimmed);
    if (nav.route[0]?.includes('repository-admin')) {
      return { ...nav, moduleLabel: 'Bóveda Digital', linkLabel: 'Abrir Bóveda Digital' };
    }
    return nav;
  }

  if (trimmed === '/documents' || trimmed.startsWith('/documents')) {
    const target = extractTargetFromUrl(trimmed);
    return {
      route: ['/layout/vault/repository-admin'],
      queryParams: target ? { target: normalizeRepositoryTaskPath(target) } : {},
      moduleLabel: 'Bóveda Digital',
      linkLabel: 'Abrir Bóveda Digital',
    };
  }

  if (
    trimmed.startsWith('/control') ||
    trimmed.includes('control-center') ||
    trimmed.startsWith('/dashboard')
  ) {
    return {
      route: ['/layout/vault/dashboard'],
      queryParams: {},
      moduleLabel: 'Centro de control',
      linkLabel: 'Abrir módulo',
    };
  }

  return {
    route: ['/layout/vault/dashboard'],
    queryParams: {},
    moduleLabel: 'Centro de control',
    linkLabel: 'Abrir módulo',
  };
}

export function resolveDashboardTaskNavigation(
  row: Record<string, unknown>,
): DashboardTaskNavigation {
  if (isFileAssignmentTask(row)) {
    const target =
      readString(row, ['assignment_open_path']) ||
      readString(row, ['navigate_target']) ||
      readString(row, ['file_path', 'repository_path', 'target_path']) ||
      extractRepositoryPathFromTask(row);
    if (target && !isDipAssignablePath(target)) {
      return {
        route: ['/layout/vault/dashboard'],
        queryParams: {},
        moduleLabel: 'Bóveda Digital',
        linkLabel: 'Asignación no disponible (solo DIP)',
      };
    }
    return {
      route: ['/layout/vault/repository-admin'],
      queryParams: target ? { target } : {},
      moduleLabel: 'Bóveda Digital',
      linkLabel: 'Abrir Bóveda Digital',
    };
  }

  const rawLink = readString(row, ['link']);
  const mapped = mapLegacyVaultLink(rawLink);
  const moduleFromRow = resolveTaskModuleLabel(row);
  return {
    ...mapped,
    moduleLabel: moduleFromRow !== 'Centro de control' ? moduleFromRow : mapped.moduleLabel,
  };
}

export function resolveTaskModuleLabel(row: Record<string, unknown>): string {
  if (isFileAssignmentTask(row)) {
    return 'Bóveda Digital';
  }

  const raw =
    readString(row, ['module']) ||
    readString(row, ['type']) ||
    readString(row, ['entity_type']) ||
    '';
  const norm = raw.toLowerCase();
  if (norm.includes('retention')) return 'Retención Documental';
  if (norm.includes('legal_hold')) return 'Custodia Legal';
  if (norm.includes('ingest')) return 'Carga de Documentos';
  if (norm.includes('audit')) return 'Auditoría';
  if (norm.includes('notification')) return 'Notificaciones';
  if (norm.includes('repository') || norm.includes('repositorio')) return 'Bóveda Digital';
  return 'Centro de control';
}

/** Fusiona tareas del API con las generadas por asignaciones activas (sin duplicados). */
export function mergeDashboardTaskRows(
  ...sources: Array<Record<string, unknown>[] | null | undefined>
): Record<string, unknown>[] {
  const seen = new Set<string>();
  const merged: Record<string, unknown>[] = [];
  for (const source of sources) {
    for (const row of source ?? []) {
      if (isFileAssignmentTask(row)) {
        const path =
          readString(row, ['assignment_open_path']) ||
          readString(row, ['file_path', 'repository_path', 'target_path']) ||
          extractRepositoryPathFromTask(row);
        if (path && !isDipAssignablePath(path)) {
          continue;
        }
      }
      const filePath = readString(row, ['file_path', 'repository_path', 'target_path']);
      const id = readString(row, ['id']);
      const key = filePath ? `path:${filePath}` : id ? `id:${id}` : null;
      if (key) {
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
      }
      merged.push(row);
    }
  }
  return merged;
}

/** Tareas de Inicio generadas desde asignaciones activas del usuario (fallback si el API no las crea). */
export function buildDashboardTasksFromFileAssignments(
  assignments: FileAssignmentRecord[],
  userId: string,
): Record<string, unknown>[] {
  const mine = filterOaisAssignableAssignments(
    resolveMyFileAssignmentsFromApi(assignments, userId),
  );
  return mine.map((row, index) => {
    const displayPath = formatRepositoryContextPath(row.file_path);
    const kind =
      row.is_directory && !isLikelyRepositoryFilePath(row.file_path)
        ? 'Carpeta asignada'
        : 'Archivo asignado';
    return {
      id: row.id || `vault-file-assignment-${index}`,
      title: `${kind} en Bóveda Digital`,
      description: `Ruta: ${displayPath}`,
      type: 'file_assignment',
      module: 'repository',
      file_path: row.file_path,
      navigate_target: resolveAssignmentEntryListPath(row) || row.file_path,
      assignment_open_path: row.file_path,
      is_directory: row.is_directory === true,
      priority: 'medium',
      created_at: row.expires_at ?? new Date().toISOString(),
    };
  });
}
